import { useEffect, useRef } from "react";
import { usePoContractsUpdateFlow } from "../../context/use-po-contracts-update-flow/use-po-contracts-update-flow";
import { Loader } from "lucide-react";
import { useBooking } from "../../hooks/use-booking";
import { useCreatePoContract } from "../../../../../hooks/useCreatePrivateOfficeContract";
import { extractErrorMessage } from "@/utils/extract-error-message";
import { getAlteredUpdateFields } from "../../context/use-po-contracts-update-flow/utils/compare-update-objects";

export const PoContractsUpdateLoading = () => {
  const {
    poContractUpdateData,
    poInitialContractData,
    upsellData,
    flowType,
    nextStep,
    poContractsUpdateFlowStatus,
    setPoContractsUpdateFlowStatus,
    setErrorMessages,
    processingStatus,
    setProcessingStatus,
  } = usePoContractsUpdateFlow();

  const { handleUpdatePoContract } = useCreatePoContract();
  const { handleCreateUpsell } = useBooking();

  const handleError = (error: unknown, type: "update" | "upsell") => {
    const errorMessage = extractErrorMessage(error, `Erro ao criar ${type}`);
    setPoContractsUpdateFlowStatus({ [type]: "error" });
    setErrorMessages({
      update: type === "update" ? errorMessage : "",
      upsell: type === "upsell" ? errorMessage : "",
    });
  };

  const createUpsell = async () => {
    try {
      if (!poContractUpdateData) {
        handleError(new Error("Dados do contrato não carregados"), "upsell");
        return false;
      }

      const cyclesToUpdate =
        upsellData?.simulatedUpsellData.updatedCycles?.map((cycle) => {
          const additionalPayments = upsellData.simulatedUpsellData.newPayments
            .filter((payment) => payment.cycleId === cycle.cycleId)
            .map((payment) => ({
              cycleId: payment.cycleId,
              amount: payment.amount,
              capacity: payment.capacity,
              paymentDate: payment.paymentDate,
              invoicingCompetencyDate: payment.invoicingCompetencyDate,
            }));

          return {
            id: cycle.cycleId,
            creditAmount: cycle.newCapacity,
            additionalPayments,
          };
        }) ?? [];

      const upsellSuccess = await handleCreateUpsell({
        companyId: poContractUpdateData.company.id,
        packageId: poContractUpdateData.packageId,
        beginDate: upsellData?.beginDate ?? poContractUpdateData.startDate,
        cyclesToUpdate,
      });

      if (!upsellSuccess.success) {
        handleError(new Error(upsellSuccess.data.message as string), "upsell");
        return false;
      }

      setPoContractsUpdateFlowStatus({ upsell: "success" });
      return true;
    } catch (error) {
      handleError(error, "upsell");
      return false;
    }
  };

  // Trava síncrona contra execução dupla: em dev o React.StrictMode monta o
  // efeito duas vezes e `processingStatus.isProcessing` (estado) ainda não
  // foi atualizado na segunda chamada — o PATCH era enviado em dobro e os
  // bookings do contrato eram recriados duas vezes.
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const createBookingFlow = async () => {
      try {
        if (!poContractUpdateData) {
          throw new Error("Dados do contrato são obrigatórios");
        }

        if (processingStatus.isProcessing) return;

        setProcessingStatus({
          isProcessing: true,
          canNavigateAway: false,
          currentOperation: "update",
        });

        const { alteredFields } = getAlteredUpdateFields(
          poContractUpdateData,
          poInitialContractData,
        );

        const poContractSuccess = await handleUpdatePoContract(
          poContractUpdateData.id,
          alteredFields,
        );

        if (!poContractSuccess) {
          setPoContractsUpdateFlowStatus({ update: "error" });
          return;
        }

        setPoContractsUpdateFlowStatus({ update: "success" });

        if (flowType === "upsell" && upsellData) {
          setProcessingStatus({
            isProcessing: true,
            canNavigateAway: false,
            currentOperation: "upsell",
          });
          const upsellSuccess = await createUpsell();
          if (!upsellSuccess) {
            setPoContractsUpdateFlowStatus({
              ...poContractsUpdateFlowStatus,
              upsell: "error",
            });
          }
        }
      } finally {
        setProcessingStatus({
          isProcessing: false,
          canNavigateAway: true,
          currentOperation: null,
        });
        nextStep();
      }
    };

    createBookingFlow();
  }, []);

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px]">
      <Loader className="h-12 w-12 animate-spin text-zinc-950" />
      <h2 className="text-xl font-semibold mt-4">
        {processingStatus.currentOperation === "update" &&
          "Editando o contrato..."}
        {processingStatus.currentOperation === "upsell" &&
          "Realizando upsell de pacotes..."}
      </h2>
      <div className="text-center text-zinc-500 text-sm font-normal font-['Inter'] leading-tight mt-4">
        Este processo pode levar alguns instantes. <br />
        Por favor, mantenha esta página aberta e não desligue seu computador.
      </div>
    </div>
  );
};
